/**
 * Event colouring for the calendar grids.
 *
 * Two facts have to be readable at once: WHOSE calendar a block belongs to,
 * and WHAT it says about their availability. The API answers with a single
 * colour carrying both — the account colour lightened by +60 when free,
 * darkened by -40 when busy — so the same person changes colour through the
 * day and two different people at the same status look alike. Neither fact
 * survives.
 *
 * Here they get their own channel: the pastel fill is the person, the vivid
 * border is the status, and a hatch pattern distinguishes the statuses that a
 * colour alone would not (never rely on colour by itself).
 */

/** Availability colours, keyed by the Zimbra free/busy code. */
export const FB_COLORS = {
  F: '#81c784', // free — green
  B: '#d32f2f', // busy — red
  T: '#fbc02d', // tentative — yellow
  O: '#e65100', // out of office — orange
  U: '#616161', // unavailable — gray
  N: '#9e9e9e', // no information — gray
};

export const FB_LABELS = {
  F: 'Libre',
  B: 'Occupé',
  T: 'À confirmer',
  O: 'Hors du bureau',
  U: 'Occupé (indisponible)',
  N: 'Inconnu',
};

/** Accepts [r,g,b], {r,g,b}, "r,g,b", "rgb(r, g, b)" or "#rrggbb". */
export function toRgb(value) {
  if (Array.isArray(value)) {
    const [r, g, b] = value.map(Number);
    return valid(r, g, b) ? { r, g, b } : null;
  }
  // An already-parsed colour: without this it stringified to "[object
  // Object]", matched nothing, and every swatch fell back to grey.
  if (value && typeof value === 'object') {
    const { r, g, b } = value;
    return valid(Number(r), Number(g), Number(b)) ? { r: Number(r), g: Number(g), b: Number(b) } : null;
  }
  const text = String(value || '').trim();
  if (text.startsWith('#')) {
    const hex = text.slice(1);
    const full = hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex;
    if (full.length !== 6) return null;
    const [r, g, b] = [0, 2, 4].map((i) => parseInt(full.substring(i, i + 2), 16));
    return valid(r, g, b) ? { r, g, b } : null;
  }
  const match = text.match(/-?\d+(\.\d+)?/g);
  if (!match || match.length < 3) return null;
  const [r, g, b] = match.slice(0, 3).map(Number);
  return valid(r, g, b) ? { r, g, b } : null;
}

function valid(r, g, b) {
  return [r, g, b].every((n) => Number.isFinite(n));
}

function css({ r, g, b }) {
  return `rgb(${r}, ${g}, ${b})`;
}

/** Mix a colour toward white. `ratio` is the share of the original kept. */
export function pastelize(color, ratio = 0.32) {
  const rgb = toRgb(color);
  if (!rgb) return color;
  const mix = (c) => Math.round(c * ratio + 255 * (1 - ratio));
  return css({ r: mix(rgb.r), g: mix(rgb.g), b: mix(rgb.b) });
}

/** Mix a colour toward black, for a border that reads against its own fill. */
export function darken(color, ratio = 0.72) {
  const rgb = toRgb(color);
  if (!rgb) return color;
  const mix = (c) => Math.round(c * ratio);
  return css({ r: mix(rgb.r), g: mix(rgb.g), b: mix(rgb.b) });
}

/** '#000' or '#fff', whichever reads better on the given background. */
export function contrastTextColor(color) {
  const rgb = toRgb(color);
  if (!rgb) return '#000';
  const lin = (c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  const luminance = 0.2126 * lin(rgb.r) + 0.7152 * lin(rgb.g) + 0.0722 * lin(rgb.b);
  return luminance > 0.36 ? '#000' : '#fff';
}

/** Hatch/opacity class for the statuses a fill colour alone cannot tell apart. */
function statusClass(code) {
  switch (code) {
    case 'F': return 'planif-fb-free';
    case 'T': return 'planif-fb-tentative';
    case 'O': return 'planif-fb-oof';
    case 'N':
    case 'U': return 'planif-fb-nodata';
    default: return '';
  }
}

/**
 * Restyle one API event for FullCalendar.
 *
 * `baseColor` is the identity colour — the account's own, resolved by the
 * caller from `resourceId`, falling back to whatever colour the API attached
 * when the resource is unknown (group member shades, for instance).
 */
export function styleEvent(event, baseColor, { solidFill = false, owner } = {}) {
  const code = String(event.fb || '').toUpperCase();
  const count = Number(event.memberCount) || 0;
  // A merged block belongs to several people at once, so nobody's colour
  // applies: it takes the availability colour instead, which is the fact it
  // actually carries.
  const merged = count > 1;
  const identity = (merged && toRgb(FB_COLORS[code]))
    || toRgb(baseColor) || toRgb(event.color) || toRgb(event.backgroundColor)
    || { r: 120, g: 144, b: 156 };
  // A pastel fill wherever a row or column already names the person. Where
  // nothing does — the week Groups grid — the colour is the only thing saying
  // whose appointment it is, so it stays vivid.
  //
  // Only for real appointments: a free/busy block carries no subject, and the
  // hatching that marks "Libre" or "Inconnu" washes out white text on a vivid
  // ground. Those keep the pastel fill and stay readable.
  const solid = solidFill && Boolean(event.title) && !merged;
  const fill = solid ? css(identity) : pastelize(css(identity));
  const border = FB_COLORS[code] || darken(css(identity));
  const cls = statusClass(code);
  const label = event.title || FB_LABELS[code] || '';

  // A merged block keeps the plain subject: the count is shown as a badge on
  // the block itself, not spelled into the title where it ate the subject.
  const title = (!merged && owner) ? `${String(owner).split('@')[0]} — ${label}` : label;

  return {
    ...event,
    title,
    backgroundColor: fill,
    borderColor: border,
    textColor: contrastTextColor(fill),
    // The band replaces the status border on a merged block; without this the
    // border would sit on top of it.
    classNames: [cls, merged ? 'planif-merged-event' : ''].filter(Boolean),
  };
}
